import { WorkspaceSimpleRoleType } from "src/clients/taktile-api";

/**
 * Role hierarchy from least to most privileged
 * Lower index = less privileged
 */
const ROLE_HIERARCHY: WorkspaceSimpleRoleType[] = [
  WorkspaceSimpleRoleType.VIEWER,
  WorkspaceSimpleRoleType.REVIEWER,
  WorkspaceSimpleRoleType.EDITOR,
  WorkspaceSimpleRoleType.ADMIN,
];

/**
 * Get the privilege level of a role (lower number = less privileged)
 */
const getRoleLevel = (role: WorkspaceSimpleRoleType): number => {
  return ROLE_HIERARCHY.indexOf(role);
};

/**
 * Check if targetRole is a downgrade from currentRole
 * (i.e., targetRole has less or equal privileges than currentRole)
 *
 * @param currentRole - The user's actual role
 * @param targetRole - The role they want to switch to
 * @returns true if targetRole is same or lower privilege than currentRole
 */
export const isRoleDowngradeOrSame = (
  currentRole: WorkspaceSimpleRoleType,
  targetRole: WorkspaceSimpleRoleType,
): boolean => {
  const currentLevel = getRoleLevel(currentRole);
  const targetLevel = getRoleLevel(targetRole);

  // Target role must be at same level or lower (less privileged)
  return targetLevel <= currentLevel;
};

/**
 * Get all roles that are equal to or lower privilege than the given role
 *
 * @param role - The reference role
 * @returns Array of roles that are equal or lower privilege
 */
export const getDowngradableRoles = (
  role: WorkspaceSimpleRoleType,
): WorkspaceSimpleRoleType[] => {
  const level = getRoleLevel(role);
  return ROLE_HIERARCHY.slice(0, level + 1);
};

/**
 * Check if a role is strictly lower privilege than another
 *
 * @param currentRole - The user's actual role
 * @param targetRole - The role they want to switch to
 * @returns true if targetRole has lower privilege than currentRole
 */
export const isRoleDowngrade = (
  currentRole: WorkspaceSimpleRoleType,
  targetRole: WorkspaceSimpleRoleType,
): boolean => {
  const currentLevel = getRoleLevel(currentRole);
  const targetLevel = getRoleLevel(targetRole);

  return targetLevel < currentLevel;
};
